import type {
  Assignment, Channel, MicType, Presenter, PresenterIcon, Session, SessionSettings,
  SessionState, CueItem, ShowMessage, Conflict,
} from '@micplot/shared';

/** '' clears the icon, so the patch type is slightly wider than Presenter. */
export type PresenterPatch =
  Omit<Partial<Presenter>, 'icon'> & { icon?: PresenterIcon | '' };

/** Identity is a participant id in localStorage -- no accounts, by design. */
export interface Identity {
  code: string;
  participantId: string;
  role: 'host' | 'editor' | 'viewer';
  name: string;
}

const KEY = 'micplot.identity';

export const loadIdentity = (): Identity | null => {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Identity) : null;
  } catch {
    return null;
  }
};

export const saveIdentity = (id: Identity | null): void => {
  if (id) localStorage.setItem(KEY, JSON.stringify(id));
  else localStorage.removeItem(KEY);
};

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  role: 'admin' | 'producer';
  photoUrl?: string;
}

export interface ShowSummary {
  code: string;
  title: string;
  hostName: string;
  createdAt: number;
  updatedAt: number;
  endedAt?: number;
  isCreator: boolean;
  presenters: number;
  channels: number;
}

export interface Production {
  id: string;
  name: string;
  notes?: string;
  createdAt: number;
  updatedAt: number;
  deletedAt?: number;
  purgesInDays?: number;
  cast: number;
  performances: number;
  isCreator: boolean;
}

export interface RosterMember {
  id: string;
  productionId: string;
  name: string;
  characterName?: string;
  title?: string;
  color: number;
  micPreference: MicType;
  icon?: PresenterIcon;
  position: number;
  headshotUrl?: string;
}

export interface Performance {
  code: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  endedAt?: number;
  presenters: number;
  channels: number;
}

export interface ShowOwner {
  userId: string;
  email: string;
  displayName: string;
  isCreator: boolean;
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const id = loadIdentity();
  const headers: Record<string, string> = { ...(init.headers as any) };
  if (init.body && !headers['content-type']) headers['content-type'] = 'application/json';
  if (id) headers['x-participant-id'] = id.participantId;

  // The login cookie is HttpOnly, so it must be sent explicitly.
  const res = await fetch(path, { ...init, headers, credentials: 'same-origin' });
  if (!res.ok) {
    const msg = await res.text().catch(() => res.statusText);
    let parsed = msg;
    try { parsed = JSON.parse(msg).message ?? msg; } catch { /* plain text */ }
    throw new Error(parsed || `Request failed (${res.status})`);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

const body = (v: unknown) => JSON.stringify(v);

export const api = {
  // ---------------------------------------------------------------- auth
  login: (email: string, password: string) =>
    req<{ user: AuthUser }>('/api/auth/login',
      { method: 'POST', body: body({ email, password }) }),

  logout: () => req('/api/auth/logout', { method: 'POST' }),

  me: () => req<{ user: AuthUser | null }>('/api/auth/me'),

  changePassword: (currentPassword: string, newPassword: string) =>
    req<{ signedOut: boolean }>('/api/auth/password',
      { method: 'POST', body: body({ currentPassword, newPassword }) }),

  setMyPhoto: (dataUrl: string) =>
    req<{ url: string }>('/api/auth/photo', { method: 'POST', body: body({ dataUrl }) }),

  // --------------------------------------------------------------- admin
  listUsers: () =>
    req<{
      id: string; email: string; displayName: string;
      role: 'admin' | 'producer'; createdAt: number; disabled: boolean;
    }[]>('/api/admin/users'),

  createUser: (email: string, password: string, displayName: string, role: string) =>
    req<{ id: string }>('/api/admin/users',
      { method: 'POST', body: body({ email, password, displayName, role }) }),

  setUserDisabled: (id: string, disabled: boolean) =>
    req(`/api/admin/users/${id}/disable`, { method: 'POST', body: body({ disabled }) }),

  setUserPassword: (id: string, password: string) =>
    req(`/api/admin/users/${id}/password`, { method: 'POST', body: body({ password }) }),

  // ------------------------------------------------------------ messages
  sendMessage: (code: string, text: string, urgent = false) =>
    req<ShowMessage>(`/api/sessions/${code}/messages`,
      { method: 'POST', body: body({ text, urgent }) }),

  ackMessage: (code: string, id: string) =>
    req<ShowMessage>(`/api/sessions/${code}/messages/${id}/ack`, { method: 'POST' }),

  // --------------------------------------------------------- productions
  listProductions: () => req<Production[]>('/api/productions'),

  listTrash: () => req<Production[]>('/api/productions/trash'),

  createProduction: (name: string, notes?: string) =>
    req<Production>('/api/productions', { method: 'POST', body: body({ name, notes }) }),

  getProduction: (id: string) =>
    req<{
      production: Production; roster: RosterMember[];
      owners: ShowOwner[]; performances: Performance[];
    }>(`/api/productions/${id}`),

  renameProduction: (id: string, name: string, notes?: string) =>
    req<Production>(`/api/productions/${id}`, { method: 'PATCH', body: body({ name, notes }) }),

  duplicateProduction: (id: string, name?: string) =>
    req<Production>(`/api/productions/${id}/duplicate`, { method: 'POST', body: body({ name }) }),

  trashProduction: (id: string) =>
    req<{ recoverableForDays: number }>(`/api/productions/${id}`, { method: 'DELETE' }),

  restoreProduction: (id: string) =>
    req<Production>(`/api/productions/${id}/restore`, { method: 'POST' }),

  purgeProduction: (id: string) =>
    req(`/api/productions/${id}/purge`, { method: 'DELETE' }),

  addCast: (productionId: string, member: Partial<RosterMember>) =>
    req<RosterMember>(`/api/productions/${productionId}/roster`,
      { method: 'POST', body: body(member) }),

  updateCast: (productionId: string, memberId: string, patch: PresenterPatch) =>
    req<RosterMember>(`/api/productions/${productionId}/roster/${memberId}`,
      { method: 'PATCH', body: body(patch) }),

  removeCast: (productionId: string, memberId: string) =>
    req(`/api/productions/${productionId}/roster/${memberId}`, { method: 'DELETE' }),

  setCastHeadshot: (productionId: string, memberId: string, dataUrl: string) =>
    req<{ member: RosterMember }>(
      `/api/productions/${productionId}/roster/${memberId}/headshot`,
      { method: 'POST', body: body({ dataUrl }) }),

  startPerformance: (productionId: string, title?: string) =>
    req<{ session: Session; participantId: string; role: 'host'; presenters: number }>(
      `/api/productions/${productionId}/performances`,
      { method: 'POST', body: body({ title }) }),

  // --------------------------------------------------------------- shows
  listShows: () => req<ShowSummary[]>('/api/shows'),

  /** Open a show you own; mints the participant record the realtime layer needs. */
  openShow: (code: string) =>
    req<{ participantId: string; role: 'host' }>(`/api/sessions/${code}/open`,
      { method: 'POST' }),

  listOwners: (code: string) => req<ShowOwner[]>(`/api/sessions/${code}/owners`),

  addOwner: (code: string, email: string) =>
    req<ShowOwner[]>(`/api/sessions/${code}/owners`,
      { method: 'POST', body: body({ email }) }),

  removeOwner: (code: string, userId: string) =>
    req<ShowOwner[]>(`/api/sessions/${code}/owners/${userId}`, { method: 'DELETE' }),

  createSession: (hostName: string, title?: string) =>
    req<{ session: Session; participantId: string; role: 'host' }>(
      '/api/sessions', { method: 'POST', body: body({ hostName, title }) }),

  join: (code: string, name: string, role: 'viewer' | 'editor') =>
    req<{ participantId: string; role: 'viewer' | 'editor'; status: string }>(
      `/api/sessions/${code}/join`, { method: 'POST', body: body({ name, role }) }),

  getState: (code: string) => req<SessionState>(`/api/sessions/${code}`),

  approve: (code: string, id: string, approve: boolean) =>
    req(`/api/sessions/${code}/participants/${id}/approve`,
      { method: 'POST', body: body({ approve }) }),

  updateSettings: (code: string, patch: Partial<SessionSettings>) =>
    req<Session>(`/api/sessions/${code}/settings`, { method: 'PATCH', body: body(patch) }),

  endSession: (code: string) => req(`/api/sessions/${code}`, { method: 'DELETE' }),

  /** Host only: the secret that lets a control surface drive the playhead. */
  getRemoteToken: (code: string) =>
    req<{ token: string }>(`/api/sessions/${code}/remote`),

  addPresenters: (code: string, count: number) =>
    req<Presenter[]>(`/api/sessions/${code}/presenters`,
      { method: 'POST', body: body({ count }) }),

  updatePresenter: (code: string, id: string, patch: PresenterPatch) =>
    req<Presenter>(`/api/sessions/${code}/presenters/${id}`,
      { method: 'PATCH', body: body(patch) }),

  deletePresenter: (code: string, id: string) =>
    req(`/api/sessions/${code}/presenters/${id}`, { method: 'DELETE' }),

  setHeadshot: (code: string, id: string, dataUrl: string) =>
    req<{ url?: string }>(`/api/sessions/${code}/presenters/${id}/headshot`,
      { method: 'POST', body: body({ dataUrl }) }),

  deleteHeadshot: (code: string, id: string) =>
    req(`/api/sessions/${code}/presenters/${id}/headshot`, { method: 'DELETE' }),

  addChannels: (code: string, count: number, micType = 'lav') =>
    req<Channel[]>(`/api/sessions/${code}/channels`,
      { method: 'POST', body: body({ count, micType }) }),

  updateChannel: (code: string, id: string, patch: Partial<Channel>) =>
    req<Channel>(`/api/sessions/${code}/channels/${id}`, { method: 'PATCH', body: body(patch) }),

  deleteChannel: (code: string, id: string) =>
    req(`/api/sessions/${code}/channels/${id}`, { method: 'DELETE' }),

  assign: (code: string, channelId: string, presenterId: string, start?: number, end?: number) =>
    req<Assignment>(`/api/sessions/${code}/assignments`,
      { method: 'POST', body: body({ channelId, presenterId, start, end }) }),

  unassign: (code: string, id: string) =>
    req(`/api/sessions/${code}/assignments/${id}`, { method: 'DELETE' }),

  autoAssign: (code: string) =>
    req<{ placed: number; unplaced: unknown[]; conflicts: Conflict[] }>(
      `/api/sessions/${code}/auto-assign`, { method: 'POST' }),

  deploy: (code: string) => req(`/api/sessions/${code}/deploy`, { method: 'POST' }),

  clearAssignments: (code: string) =>
    req<{ cleared: number }>(`/api/sessions/${code}/assignments/clear`, { method: 'POST' }),

  showReport: (code: string) =>
    req<{
      title: string;
      events: {
        id: string; at: number; kind: string; severity: string;
        channelNumber?: number; presenter?: string; message: string; clearedAt?: number;
      }[];
      channels: {
        number: number; label?: string; frequency?: number; micType: string;
        samples: number; dropouts: number; minRfLevel?: number; minBatteryMinutes?: number;
        antennaA: number; antennaB: number; incidents: number; worst: string;
      }[];
      totals: { incidents: number; critical: number; dropouts: number };
    }>(`/api/sessions/${code}/report`),

  undo: (code: string) =>
    req<{ ok: true; undone: { label: string; actor?: string } }>(
      `/api/sessions/${code}/undo`, { method: 'POST' }),

  setStepAssignments: (code: string, stepId: string,
                       assignments: { channelId: string; presenterId: string }[]) =>
    req<CueItem>(`/api/sessions/${code}/steps/${stepId}/assignments`,
      { method: 'PUT', body: body({ assignments }) }),

  applyStep: (code: string, stepId: string) =>
    req<{ applied: number }>(`/api/sessions/${code}/steps/${stepId}/apply`, { method: 'POST' }),

  addStep: (code: string, cueId: string, label?: string) =>
    req<{ id: string }>(`/api/sessions/${code}/cues/${cueId}/steps`,
      { method: 'POST', body: body({ label }) }),

  removeStep: (code: string, stepId: string) =>
    req(`/api/sessions/${code}/steps/${stepId}`, { method: 'DELETE' }),

  applyCueTemplate: (code: string, templateId: string, curtain: number) =>
    req<{ template: string; items: number }>(`/api/sessions/${code}/cues/template`,
      { method: 'POST', body: body({ templateId, curtain }) }),

  addCue: (code: string, cue: { label: string; kind?: string; ensemble?: string; notes?: string }) =>
    req<CueItem>(`/api/sessions/${code}/cues`, { method: 'POST', body: body(cue) }),

  removeCue: (code: string, cueId: string) =>
    req(`/api/sessions/${code}/cues/${cueId}`, { method: 'DELETE' }),

  updateCue: (code: string, cueId: string,
              patch: { label?: string; kind?: string; ensemble?: string; notes?: string }) =>
    req<CueItem>(`/api/sessions/${code}/cues/${cueId}`,
      { method: 'PATCH', body: body(patch) }),

  reorderCue: (code: string, cueId: string, toIndex: number) =>
    req<CueItem[]>(`/api/sessions/${code}/cues/${cueId}/reorder`,
      { method: 'POST', body: body({ toIndex }) }),

  setMode: (code: string, mode: string) =>
    req(`/api/sessions/${code}/settings`, { method: 'PATCH', body: body({ mode }) }),

  setCues: (code: string, cues: Partial<CueItem>[]) =>
    req<CueItem[]>(`/api/sessions/${code}/cues`, { method: 'PUT', body: body({ cues }) }),

  playhead: (code: string, action: 'next' | 'prev' | 'deploy' | 'goto', pos?: number) =>
    req<{ position: number; total: number; cue: string | null }>(
      `/api/sessions/${code}/playhead/${action}${pos !== undefined ? `?pos=${pos}` : ''}`,
      { method: 'POST' }),

  exportShow: async (code: string, name: string): Promise<void> => {
    const res = await fetch(`/api/sessions/${code}/show?name=${encodeURIComponent(name)}`);
    if (!res.ok) throw new Error('Could not export the show.');
    const blob = await res.blob();
    // Anchor download rather than navigation, so the session stays connected.
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${name.replace(/[^a-zA-Z0-9 _-]/g, '').trim() || 'show'}.micplot.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },

  importShow: (code: string, show: unknown) =>
    req<{ name: string; presenters: number; channels: number; cues: number }>(
      `/api/sessions/${code}/show`, { method: 'POST', body: body({ show }) }),

  importWWB: (code: string, text: string) =>
    req<{ imported: number }>(`/api/sessions/${code}/import/wwb`,
      { method: 'POST', body: body({ text }) }),

  importRundown: (code: string, text: string) =>
    req<{ presenters: number; cues: number }>(`/api/sessions/${code}/import/rundown`,
      { method: 'POST', body: body({ text }) }),
};
